import { Box, FlexProps, Flex, Icon, Text, Tooltip } from 'pounceV2';
import React from 'react';
import useRouter from 'Hooks/useRouter';
import { addTrailingSlash, getPathnameFromURI } from 'Helpers/utils';
import { Link as RRLink } from 'react-router-dom';
import type { IconType } from 'pounceV2/src/theme/icons';
import { useTheme } from '@mui/material';
import { NavigationLink, SecondaryNavBaseProps } from '../../types';
import { NAV_ICON_SIZE, NAV_ROW_SIZE, railCenterOffset } from '../../constants';

type SecondaryNavLinkProps = NavigationLink & SecondaryNavBaseProps;

const SideNavLinkV3 = ({
  icon,
  testId,
  label,
  to,
  exact,
  activePaths,
  isSecondary,
  isNavExpanded,
  trailing,
  ...flexProps
}: SecondaryNavLinkProps & FlexProps) => {
  const { location } = useRouter();
  const theme = useTheme();
  const pathname = addTrailingSlash(location.pathname);
  const destination = addTrailingSlash(getPathnameFromURI(to));
  const extraDestinations = (activePaths ?? []).map(path =>
    addTrailingSlash(getPathnameFromURI(path)),
  );
  const isActive = exact
    ? pathname === destination
    : pathname.startsWith(destination) || extraDestinations.some(d => pathname.startsWith(d));

  // Rows are square in the collapsed rail (side-nav-sm 40 / sub-nav 32), fill it when expanded.
  const rowSize = isSecondary ? 32 : NAV_ROW_SIZE;
  // 7px (not 6) aligns the expanded icon with the centered collapsed icon; collapsed centers on the rail axis.
  const expandedMargin = isSecondary ? 0 : '7px';
  const marginLeft = isNavExpanded ? expandedMargin : railCenterOffset(rowSize);

  const link = (
    <Box
      as={RRLink}
      to={to}
      data-tid={testId}
      aria-current={isActive ? 'page' : undefined}
      aria-label={`Link to ${label} page`}
    >
      <Flex
        as="span"
        alignItems="center"
        justifyContent="space-between"
        ml={marginLeft}
        mr={isNavExpanded ? marginLeft : 0}
        truncated
        {...flexProps}
      >
        <Flex
          as="span"
          alignItems="center"
          justifyContent={isNavExpanded ? 'flex-start' : 'center'}
          px={isNavExpanded ? 3 : 2}
          py={2}
          borderRadius="large"
          height={rowSize}
          width={isNavExpanded ? '100%' : rowSize}
          color={
            isActive
              ? theme.palette.componentTokens.navigation.colors.text.selected
              : theme.palette.componentTokens.navigation.colors.text.resting
          }
          backgroundColor={
            isActive
              ? theme.palette.componentTokens.navigation.colors.backgroundRowItems.selected
              : 'transparent'
          }
          opacity={isSecondary && !isActive ? '.85' : '1'}
          transition="background-color 250ms ease"
          _hover={
            !isActive && {
              backgroundColor:
                theme.palette.componentTokens.navigation.colors.backgroundRowItems.hover,
              opacity: '1',
              color: theme.palette.componentTokens.navigation.colors.text.hover,
            }
          }
        >
          {/* icon */}
          {isSecondary && !isNavExpanded && (
            <Icon type={icon as IconType} opacity={!isActive ? 0.65 : 1} size={NAV_ICON_SIZE} />
          )}
          {!isSecondary && (
            <Icon type={icon as IconType} size={NAV_ICON_SIZE} mr={isNavExpanded ? 3 : 0} />
          )}

          {/* label */}
          {isNavExpanded && (
            <Text
              as="span"
              variant="body/md"
              fontWeight="medium"
              sx={{
                color: isActive
                  ? theme.palette.componentTokens.navigation.colors.text.selected
                  : theme.palette.componentTokens.navigation.colors.text.resting,
              }}
            >
              {label}
            </Text>
          )}

          {/* trailing slot (e.g. "New" chip) */}
          {isNavExpanded && trailing && <Box ml="auto">{trailing}</Box>}
        </Flex>
      </Flex>
    </Box>
  );

  return isNavExpanded ? link : <Tooltip content={label}>{link}</Tooltip>;
};

export default SideNavLinkV3;
